import { isApiError, unwrap, type PermissionGroupOut, type RoleOut } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Field, Icon, Input, Textarea, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { useApi } from "../session/session-provider";
import { useWorkspaceContext } from "../session/workspace-context";
import { EffectivePermissions } from "./effective-permissions";
import { sameSet } from "./matrix-model";
import { PermissionMatrix } from "./permission-matrix";
import { useEffectivePreview, useInvalidateAccess } from "./use-roles";

export interface RoleEditorProps {
  /** Undefined = create mode. */
  role: RoleOut | undefined;
  /** Permissions to start a new role from (blank or a copy). */
  initialPermissions?: readonly string[];
  groups: readonly PermissionGroupOut[];
  grantable: ReadonlySet<string>;
  canManage: boolean;
  planAllowed: boolean;
  onPlanGate: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
}

interface Draft {
  name: string;
  description: string;
  permissions: Set<string>;
}

function useSaveRole(role: RoleOut | undefined, onPlanGate: () => void) {
  const api = useApi();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const invalidate = useInvalidateAccess();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (draft: Draft) => {
      const body = { name: draft.name.trim(), description: draft.description.trim() || null, permissions: [...draft.permissions].sort() };
      if (role) return unwrap(api.PATCH("/v1/roles/{role_id}", { params: { path: { role_id: role.id } }, body }));
      return unwrap(api.POST("/v1/roles", { body }));
    },
    onSuccess: async (saved) => {
      toast.success(role ? t("roles.editor.saved") : t("roles.editor.created", { name: saved.name }));
      await invalidate();
      if (!role) await navigate(consoleRoutes.roles(summary.slug, saved.id), { replace: true });
    },
    onError: (caught) => {
      if (isApiError(caught) && caught.isPlanGate) onPlanGate();
    },
  });
}

function LockedNote({ onDuplicate, planAllowed }: { onDuplicate?: () => void; planAllowed: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 rounded-md border border-line bg-surface-2 p-3 text-xs text-fg-muted sm:flex-row sm:items-center">
      <Icon name="lock" className="size-4 shrink-0 text-fg-subtle" />
      <span className="flex-1">{t("roles.lockedHint")}</span>
      {onDuplicate ? (
        <Button variant="secondary" size="sm" disabled={!planAllowed} onClick={onDuplicate}>
          <Icon name="copy" />
          {t("roles.duplicate")}
        </Button>
      ) : null}
    </div>
  );
}

/** Role details + grouped permission matrix + server-checked effective preview + save. */
export function RoleEditor({ role, initialPermissions = [], groups, grantable, canManage, planAllowed, onPlanGate, onDuplicate, onDelete }: RoleEditorProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { summary } = useWorkspaceContext();
  const initial: Draft = { name: role?.name ?? "", description: role?.description ?? "", permissions: new Set(role?.permissions ?? initialPermissions) };
  const [draft, setDraft] = useState<Draft>(initial);
  const save = useSaveRole(role, onPlanGate);
  const locked = role?.is_locked ?? false;
  const readOnly = locked || !canManage || !planAllowed;
  const total = groups.reduce((n, g) => n + g.permissions.length, 0);
  const dirty = draft.name !== initial.name || draft.description !== initial.description || !sameSet(draft.permissions, initial.permissions);
  const preview = useEffectivePreview({ permissions: [...draft.permissions] });
  const nameMissing = draft.name.trim().length === 0;

  return (
    <div className="space-y-6">
      <section className="card" aria-labelledby="role-details-title">
        <div className="card-header">
          <div className="flex min-w-0 items-center gap-2">
            <h2 id="role-details-title" className="card-title truncate">
              {role ? role.name : t("roles.editor.createTitle")}
            </h2>
            {role?.is_builtin ? <Badge tone="neutral">{t("roles.builtIn")}</Badge> : null}
            {dirty && !readOnly ? <Badge tone="warning">{t("roles.editor.unsaved")}</Badge> : null}
          </div>
          {role && !locked && canManage && onDelete ? (
            <Button variant="ghost" size="sm" className="text-danger" onClick={onDelete}>
              <Icon name="trash" />
              {t("roles.delete")}
            </Button>
          ) : null}
        </div>
        <div className="card-body space-y-4">
          {locked ? <LockedNote onDuplicate={canManage ? onDuplicate : undefined} planAllowed={planAllowed} /> : null}
          {save.isError && !(isApiError(save.error) && save.error.isPlanGate) ? <FormAlert title={message(save.error)} onDismiss={() => { save.reset(); }} /> : null}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label={t("roles.editor.name")} error={!readOnly && dirty && nameMissing ? t("roles.editor.nameRequired") : undefined}>
              <Input value={draft.name} disabled={readOnly} placeholder={t("roles.editor.namePlaceholder")} maxLength={120} onChange={(e) => { setDraft({ ...draft, name: e.target.value }); }} />
            </Field>
            <Field label={t("roles.editor.description")}>
              <Textarea rows={2} value={draft.description} disabled={readOnly} placeholder={t("roles.editor.descriptionPlaceholder")} maxLength={2000} onChange={(e) => { setDraft({ ...draft, description: e.target.value }); }} />
            </Field>
          </div>
        </div>
      </section>
      <section className="card overflow-hidden" aria-labelledby="role-matrix-title">
        <div className="card-header">
          <div>
            <h2 id="role-matrix-title" className="card-title">
              {t("roles.matrix.title")}
            </h2>
            <p className="mt-0.5 text-xs text-fg-subtle">{t("roles.matrix.subtitle")}</p>
          </div>
        </div>
        <PermissionMatrix groups={groups} value={draft.permissions} onChange={(permissions) => { setDraft({ ...draft, permissions }); }} readOnly={readOnly} grantable={grantable} />
        <div className="border-t border-line p-5">
          <EffectivePermissions preview={preview.data} isPending={preview.isFetching} isError={preview.isError} total={total} />
        </div>
        <div className="flex flex-col gap-3 border-t border-line bg-surface-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-2 text-xs text-fg-muted">
            <Icon name="scroll-text" className="size-4 text-fg-subtle" />
            {t("roles.editor.auditNote")}{" "}
            <AppLink href={consoleRoutes.settings(summary.slug, "audit")} className="font-semibold text-primary-ink hover:underline">
              {t("nav.audit")}
            </AppLink>
          </p>
          {readOnly ? null : (
            <div className="flex gap-2">
              <Button variant="ghost" disabled={!dirty || save.isPending} onClick={() => { setDraft(initial); }}>
                {t("roles.editor.discard")}
              </Button>
              <Button loading={save.isPending} disabled={!dirty || nameMissing || save.isPending} onClick={() => { save.mutate(draft); }}>
                <Icon name="check" />
                {role ? t("roles.editor.save") : t("roles.editor.create")}
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
